import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  ImageOff,
  Layers3,
  Loader2,
  PackagePlus,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

const initialForm = {
  combo_name: "",
  combo_price: "",
  combo_description: "",
  combo_type: "Configurable",
  is_configurable: "Y",
  is_active: "Y",
};

const createInitialGroups = () => [
  {
    localId: "pizza",
    itemSelectionName: "Choose Your Pizza",
    minimumSelection: "1",
    maximumSelection: "1",
    isConfigurable: "Y",
  },
  {
    localId: "burger",
    itemSelectionName: "Choose Your Burger",
    minimumSelection: "1",
    maximumSelection: "1",
    isConfigurable: "N",
  },
  {
    localId: "cold-drink",
    itemSelectionName: "Choose Your Cold Drink",
    minimumSelection: "1",
    maximumSelection: "1",
    isConfigurable: "N",
  },
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const toText = (value) => String(value ?? "").trim();

const getSelectionRule = (group = {}) => {
  const min = String(
    group?.minimum_selection ?? group?.minimumSelection ?? "0",
  );
  const max = String(
    group?.maximum_selection ?? group?.maximumSelection ?? "0",
  );

  if (min === max) return `Choose exactly ${min}`;
  return `Choose ${min} to ${max}`;
};

const getPriceLabel = (price = {}) => {
  if (price?.formatted_price) return String(price.formatted_price);

  const value =
    price?.price ??
    price?.selling_price ??
    price?.takeaway_price ??
    price?.takewayPrice;

  if (value !== null && value !== undefined && value !== "") {
    return `₹${value}`;
  }

  return "—";
};

const ComboTab = ({
  comboList = [],
  searchText = "",
  comboOfferSaving = false,
  optionGroupSaving = false,
  comboConfigurableSaving = false,
  catalogV3Loading = false,

  insertComboOffer,
  getComboOffer,
  insertComboOptionGroupDetails,
  getOptionGroupItems,
  insertComboConfigurable,
  getCatalogItemsV3,

  userID = "",
  catalogueItemsFallback = [],

  onMasterRefresh,
}) => {
  const [form, setForm] = useState(initialForm);

  // Workflow:
  // 1 = create combo
  // 2 = option groups
  // 3 = map items
  const [workflowStep, setWorkflowStep] = useState(1);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [createdCombo, setCreatedCombo] = useState(null);

  const [draftGroups, setDraftGroups] = useState(createInitialGroups);
  const [savedGroups, setSavedGroups] = useState([]);
  const [groupSelections, setGroupSelections] = useState({});

  // Latest getComboOffer response.
  // Used during the creation flow before master catalogue is refreshed.
  const [getComboOfferResponse, setGetComboOfferResponse] = useState([]);

  // Final getCatalogueItemsMaster comboList.
  // This has the complete optionGroup -> items -> prices/customizations data.
  const [masterComboResponse, setMasterComboResponse] = useState([]);

  const [expandedComboId, setExpandedComboId] = useState(null);

  // Persist current workflow across refresh and Catalogue tab switching.
  const hydratedRef = useRef(false);

  const storageKey = useMemo(
    () => `pos_combo_old_ui_progress_${String(userID || "guest")}`,
    [userID],
  );

  // Item selector modal
  const [showItemSelector, setShowItemSelector] = useState(false);
  const [targetGroup, setTargetGroup] = useState(null);
  const [itemSearchText, setItemSearchText] = useState("");

  // =========================================================
  // RESTORE OLD UI PROGRESS
  // =========================================================

  useEffect(() => {
    hydratedRef.current = false;

    try {
      const raw = localStorage.getItem(storageKey);

      if (!raw) {
        hydratedRef.current = true;
        return;
      }

      const saved = JSON.parse(raw);

      if (saved?.form && typeof saved.form === "object") {
        setForm({
          ...initialForm,
          ...saved.form,
        });
      }

      const savedStep = Number(saved?.workflowStep);

      if (savedStep >= 1 && savedStep <= 3) {
        setWorkflowStep(savedStep);
      }

      if (saved?.createdCombo && typeof saved.createdCombo === "object") {
        setCreatedCombo(saved.createdCombo);
      }

      if (Array.isArray(saved?.draftGroups)) {
        setDraftGroups(saved.draftGroups);
      }

      if (Array.isArray(saved?.savedGroups)) {
        setSavedGroups(saved.savedGroups);
      }

      if (saved?.groupSelections && typeof saved.groupSelections === "object") {
        setGroupSelections(saved.groupSelections);
      }

      if (Array.isArray(saved?.getComboOfferResponse)) {
        setGetComboOfferResponse(saved.getComboOfferResponse);
      }

      if (Array.isArray(saved?.masterComboResponse)) {
        setMasterComboResponse(saved.masterComboResponse);
      }

      if (saved?.expandedComboId) {
        setExpandedComboId(String(saved.expandedComboId));
      }
    } catch (error) {
      console.error("Combo old UI progress restore error:", error);
    } finally {
      hydratedRef.current = true;
    }
  }, [storageKey]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          form,
          workflowStep,
          createdCombo,
          draftGroups,
          savedGroups,
          groupSelections,
          getComboOfferResponse,
          masterComboResponse,
          expandedComboId,
        }),
      );
    } catch (error) {
      console.error("Combo old UI progress save error:", error);
    }
  }, [
    storageKey,
    form,
    workflowStep,
    createdCombo,
    draftGroups,
    savedGroups,
    groupSelections,
    getComboOfferResponse,
    masterComboResponse,
    expandedComboId,
  ]);

  const safeComboList = useMemo(
    () => (Array.isArray(comboList) ? comboList : []),
    [comboList],
  );

  const displayComboList = useMemo(() => {
    // After final getCatalogueItemsMaster, always prefer its full comboList.
    if (Array.isArray(masterComboResponse) && masterComboResponse.length > 0) {
      return masterComboResponse;
    }

    // During create flow, show getComboOffer immediately.
    if (
      Array.isArray(getComboOfferResponse) &&
      getComboOfferResponse.length > 0
    ) {
      return getComboOfferResponse;
    }

    return safeComboList;
  }, [masterComboResponse, getComboOfferResponse, safeComboList]);

  const extractMasterComboList = (response) => {
    const candidates = [
      response?.comboList,
      response?.response?.comboList,
      response?.data?.comboList,
      response?.data?.response?.comboList,
      response?.result?.comboList,
      response?.response?.response?.comboList,
    ];

    const found = candidates.find(Array.isArray);

    return Array.isArray(found) ? found : [];
  };

  const filteredCombos = useMemo(() => {
    const query = String(searchText || "")
      .trim()
      .toLowerCase();

    if (!query) return displayComboList;

    return displayComboList.filter((combo) => {
      const text = [
        combo?.combo_name,
        combo?.combo_description,
        combo?.combo_type,
        combo?.combo_price,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");

      return text.includes(query);
    });
  }, [displayComboList, searchText]);

  const configurableComboCount = useMemo(
    () =>
      displayComboList.filter(
        (combo) => String(combo?.is_configurable || "").toUpperCase() === "Y",
      ).length,
    [displayComboList],
  );

  // ---------------------------------------------------------
  // CURRENT CATALOGUE ITEMS
  // ---------------------------------------------------------

  const flatCatalogueItems = useMemo(() => {
    const source = asArray(catalogueItemsFallback);

    const rows = source.flatMap((category) => {
      if (Array.isArray(category?.menu_items)) {
        return category.menu_items.map((item) => ({
          ...item,
          __categoryName:
            category?.category_name ||
            category?.categoryName ||
            category?.name ||
            "",
        }));
      }

      if (Array.isArray(category?.items)) {
        return category.items.map((item) => ({
          ...item,
          __categoryName:
            category?.category_name ||
            category?.categoryName ||
            category?.name ||
            "",
        }));
      }

      return [category];
    });

    const seen = new Set();

    return rows.filter((item) => {
      const itemId = toText(
        item?.item_id ?? item?.itemId ?? item?.pk_item_id ?? item?.id,
      );

      if (!itemId || seen.has(itemId)) return false;

      seen.add(itemId);
      return true;
    });
  }, [catalogueItemsFallback]);

  const getItemId = (item) =>
    toText(item?.item_id ?? item?.itemId ?? item?.pk_item_id ?? item?.id);

  const getItemName = (item) =>
    toText(
      item?.item_name ??
        item?.itemName ??
        item?.productName ??
        item?.name ??
        "Unnamed item",
    );

  const getItemImage = (item) =>
    item?.photo || item?.itemImage || item?.image || item?.item_image || "";

  const getItemPrices = (item) =>
    Array.isArray(item?.prices)
      ? item.prices
      : Array.isArray(item?.priceList)
        ? item.priceList
        : [];

  const filteredSelectableItems = useMemo(() => {
    const query = String(itemSearchText || "")
      .trim()
      .toLowerCase();

    if (!query) return flatCatalogueItems;

    return flatCatalogueItems.filter((item) => {
      const prices = getItemPrices(item);

      const text = [
        getItemName(item),
        getItemId(item),
        item?.__categoryName,
        item?.sub_category,
        ...prices.map(
          (price) =>
            price?.size || price?.variantName || price?.variant_name || "",
        ),
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ");

      return text.includes(query);
    });
  }, [flatCatalogueItems, itemSearchText]);

  // ---------------------------------------------------------
  // STEP 1
  // insertComboOffer -> getComboOffer -> resolve fk combo ID
  // ---------------------------------------------------------

  const resolveCreatedCombo = (offers, submittedForm) => {
    const safeOffers = asArray(offers);

    const sameName = safeOffers.filter(
      (combo) =>
        toText(combo?.combo_name).toLowerCase() ===
        toText(submittedForm?.combo_name).toLowerCase(),
    );

    if (!sameName.length) return null;

    const exactMatches = sameName.filter((combo) => {
      const samePrice =
        toText(combo?.combo_price) === toText(submittedForm?.combo_price);

      const sameDescription =
        toText(combo?.combo_description).toLowerCase() ===
        toText(submittedForm?.combo_description).toLowerCase();

      const sameType =
        toText(combo?.combo_type).toLowerCase() ===
        toText(submittedForm?.combo_type).toLowerCase();

      const sameConfigurable =
        toText(combo?.is_configurable).toUpperCase() ===
        toText(submittedForm?.is_configurable).toUpperCase();

      return samePrice && sameDescription && sameType && sameConfigurable;
    });

    const candidates = exactMatches.length > 0 ? exactMatches : sameName;

    return [...candidates].sort(
      (a, b) => Number(b?.combo_id || 0) - Number(a?.combo_id || 0),
    )[0];
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateCombo = async (event) => {
    event.preventDefault();

    if (!form.combo_name.trim()) {
      toast.error("Please enter combo name");
      return;
    }

    if (!form.combo_price.trim()) {
      toast.error("Please enter combo price");
      return;
    }

    setWorkflowBusy(true);

    try {
      // FLOW 1
      const inserted = await insertComboOffer?.({
        combo_name: form.combo_name.trim(),
        combo_price: form.combo_price.trim(),
        combo_description: form.combo_description.trim(),
        combo_type: form.combo_type.trim(),
        is_configurable: form.is_configurable,
        is_active: form.is_active,
      });

      if (!inserted) return;

      const offers = await getComboOffer?.(form.combo_name.trim(), {
        force: true,
      });

      setMasterComboResponse([]);

      // Map getComboOffer API response in the SAME old right-side Combo List UI.
      setGetComboOfferResponse(Array.isArray(offers) ? offers : []);

      const resolvedCombo = resolveCreatedCombo(offers, form);

      if (!resolvedCombo?.combo_id) {
        toast.error("Combo inserted but new combo ID could not be resolved");
        return;
      }

      setCreatedCombo(resolvedCombo);
      setDraftGroups(createInitialGroups());
      setSavedGroups([]);
      setGroupSelections({});
      setWorkflowStep(2);

      // toast.success(
      //   `Combo ID ${resolvedCombo.combo_id} selected. Add option groups.`,
      // );
    } catch (error) {
      console.error("Create combo flow error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Unable to start combo setup",
      );
    } finally {
      setWorkflowBusy(false);
    }
  };

  // ---------------------------------------------------------
  // STEP 2
  // insertComboOptionGroupDetails -> getOptionGroupItems
  // ---------------------------------------------------------

  const updateDraftGroup = (localId, field, value) => {
    setDraftGroups((prev) =>
      prev.map((group) =>
        group.localId === localId
          ? {
              ...group,
              [field]: value,
            }
          : group,
      ),
    );
  };

  const addDraftGroup = () => {
    setDraftGroups((prev) => [
      ...prev,
      {
        localId: `group-${Date.now()}-${prev.length}`,
        itemSelectionName: "",
        minimumSelection: "1",
        maximumSelection: "1",
        // Default requested by user.
        isConfigurable: "N",
      },
    ]);
  };

  const removeDraftGroup = (localId) => {
    setDraftGroups((prev) => prev.filter((group) => group.localId !== localId));
  };

  const validateGroups = () => {
    if (!draftGroups.length) {
      toast.error("Please add at least one option group");
      return false;
    }

    for (const group of draftGroups) {
      if (!toText(group.itemSelectionName)) {
        toast.error("Option group name is required");
        return false;
      }

      const minimum = Number(group.minimumSelection);
      const maximum = Number(group.maximumSelection);

      if (
        !Number.isFinite(minimum) ||
        !Number.isFinite(maximum) ||
        minimum < 0 ||
        maximum < 0
      ) {
        toast.error(`Invalid selection limit for ${group.itemSelectionName}`);
        return false;
      }

      if (maximum < minimum) {
        toast.error(
          `Maximum selection cannot be less than minimum for ${group.itemSelectionName}`,
        );
        return false;
      }
    }

    return true;
  };

  const handleSaveOptionGroups = async () => {
    if (!createdCombo?.combo_id) {
      toast.error("Resolved combo ID missing");
      return;
    }

    if (!validateGroups()) return;

    setWorkflowBusy(true);

    try {
      // FLOW 3
      // Add groups sequentially so API order remains predictable.
      for (const group of draftGroups) {
        const success = await insertComboOptionGroupDetails?.({
          fkComboId: String(createdCombo.combo_id),
          itemSelectionName: group.itemSelectionName.trim(),
          minimumSelection: String(group.minimumSelection),
          maximumSelection: String(group.maximumSelection),
          // N is preselected; user can change it to Y.
          isConfigurable: group.isConfigurable || "N",
        });

        if (!success) {
          toast.error(`Stopped while creating ${group.itemSelectionName}`);
          return;
        }
      }

      // FLOW 4
      const fetchedGroups = await getOptionGroupItems?.(
        String(createdCombo.combo_id),
      );

      if (!Array.isArray(fetchedGroups)) {
        toast.error("Option groups created but group IDs were not returned");
        return;
      }

      const wantedNames = new Set(
        draftGroups.map((group) =>
          toText(group.itemSelectionName).toLowerCase(),
        ),
      );

      const matchingGroups = fetchedGroups.filter((group) =>
        wantedNames.has(
          toText(
            group?.item_selection_name || group?.itemSelectionName,
          ).toLowerCase(),
        ),
      );

      const finalGroups =
        matchingGroups.length > 0 ? matchingGroups : fetchedGroups;

      setSavedGroups(finalGroups);
      setWorkflowStep(3);

      // toast.success("Option groups created. Now select items for each group.");
    } catch (error) {
      console.error("Save option groups flow error:", error);
      toast.error("Unable to create option groups");
    } finally {
      setWorkflowBusy(false);
    }
  };

  const getGroupId = (group) =>
    toText(
      group?.pk_option_group_item_id ??
        group?.option_group_item_id ??
        group?.id,
    );

  const getGroupName = (group) =>
    toText(
      group?.item_selection_name ??
        group?.itemSelectionName ??
        group?.groupTitle ??
        "Option Group",
    );

  const openItemSelector = (group) => {
    const groupId = getGroupId(group);

    if (!groupId) {
      toast.error("Option group item ID missing");
      return;
    }

    setTargetGroup(group);
    setItemSearchText("");
    setShowItemSelector(true);
  };

  const closeItemSelector = () => {
    setShowItemSelector(false);
    setTargetGroup(null);
    setItemSearchText("");
  };

  const toggleGroupItem = (groupId, itemId) => {
    const finalGroupId = toText(groupId);
    const finalItemId = toText(itemId);

    if (!finalGroupId || !finalItemId) return;

    setGroupSelections((prev) => {
      const current = Array.isArray(prev[finalGroupId])
        ? prev[finalGroupId]
        : [];

      return {
        ...prev,
        [finalGroupId]: current.includes(finalItemId)
          ? current.filter((id) => id !== finalItemId)
          : [...current, finalItemId],
      };
    });
  };

  const handleFinishComboSetup = async () => {
    if (!createdCombo?.combo_id) {
      toast.error("Combo ID missing");
      return;
    }

    if (!savedGroups.length) {
      toast.error("Option groups missing");
      return;
    }

    // Require item selection for every group.
    for (const group of savedGroups) {
      const groupId = getGroupId(group);
      const selectedIds = asArray(groupSelections[groupId]);

      if (!selectedIds.length) {
        toast.error(`Please select items for ${getGroupName(group)}`);
        return;
      }
    }

    setWorkflowBusy(true);

    try {
  
      for (const group of savedGroups) {
        const groupId = getGroupId(group);
        const selectedIds = asArray(groupSelections[groupId]);

        const success = await insertComboConfigurable?.({
          user_id: String(userID),
          combo_id: String(createdCombo.combo_id),
          option_group_item_id: groupId,
          item_id: selectedIds,
        });

        if (!success) {
          toast.error(`Stopped while saving items for ${getGroupName(group)}`);
          return;
        }
      }

      const v3Result = await getCatalogItemsV3?.({
        userid: String(userID),
        orderType: "takeaway",
        platform: "pos",
      });

      if (!v3Result) {
        toast.error("Combo saved, but v3 catalogue refresh failed");
        return;
      }

      const masterResult = await onMasterRefresh?.();

      const masterCombos = extractMasterComboList(masterResult);

      console.log("FLOW 7 - MAPPED MASTER COMBO LIST:", masterCombos);

      if (masterCombos.length > 0) {
        setMasterComboResponse(masterCombos);
      }

      setGetComboOfferResponse([]);
      const completedComboId = String(createdCombo.combo_id);

      setExpandedComboId(completedComboId);

      try {
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.error("Combo progress clear error:", error);
      }

      setForm(initialForm);
      setCreatedCombo(null);
      setDraftGroups(createInitialGroups());
      setSavedGroups([]);
      setGroupSelections({});
      setWorkflowStep(1);
    } catch (error) {
      console.error("Finish combo setup error:", error);
      toast.error("Unable to complete combo setup");
    } finally {
      setWorkflowBusy(false);
    }
  };

  const resetWorkflow = () => {
    if (
      workflowBusy ||
      comboOfferSaving ||
      optionGroupSaving ||
      comboConfigurableSaving ||
      catalogV3Loading
    ) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error("Combo progress clear error:", error);
    }

    setForm(initialForm);
    setCreatedCombo(null);
    setDraftGroups(createInitialGroups());
    setSavedGroups([]);
    setGroupSelections({});
    setGetComboOfferResponse([]);
    setMasterComboResponse([]);
    setWorkflowStep(1);
    closeItemSelector();
  };

  const stepBusy =
    workflowBusy ||
    comboOfferSaving ||
    optionGroupSaving ||
    comboConfigurableSaving ||
    catalogV3Loading;

  return (
    <>
      <div className="grid min-h-full grid-cols-1 gap-5 pb-5 xl:grid-cols-[430px_minmax(0,1fr)]">
        {/* =====================================================
            LEFT - CREATE COMBO WORKFLOW
           ===================================================== */}
        <div className="h-fit rounded-2xl border bg-white shadow-sm xl:sticky xl:top-0">
          {/* Steps */}
          <div className="border-b p-5">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((step) => {
                const done = workflowStep > step;
                const active = workflowStep === step;

                return (
                  <React.Fragment key={step}>
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        done
                          ? "bg-green-600 text-white"
                          : active
                            ? "bg-green-100 text-green-700 ring-2 ring-green-500"
                            : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {done ? <Check size={15} /> : step}
                    </div>

                    {step < 3 && (
                      <div
                        className={`h-0.5 flex-1 ${
                          workflowStep > step ? "bg-green-500" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="mt-3 grid grid-cols-3 text-center text-[10px] font-semibold">
              <span
                className={
                  workflowStep === 1 ? "text-green-700" : "text-gray-500"
                }
              >
                Combo
              </span>
              <span
                className={
                  workflowStep === 2 ? "text-green-700" : "text-gray-500"
                }
              >
                Groups
              </span>
              <span
                className={
                  workflowStep === 3 ? "text-green-700" : "text-gray-500"
                }
              >
                Items
              </span>
            </div>
          </div>

          {/* STEP 1 */}
          {workflowStep === 1 && (
            <div className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-100 text-green-700">
                  <PackagePlus size={17} />
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Create Combo
                  </h3>
                </div>
              </div>

              <form onSubmit={handleCreateCombo} className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Name
                  </label>
                  <input
                    name="combo_name"
                    value={form.combo_name}
                    onChange={handleChange}
                    placeholder="e.g. Birthday Combo"
                    className="h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Price
                  </label>
                  <input
                    name="combo_price"
                    value={form.combo_price}
                    onChange={handleChange}
                    placeholder="499"
                    inputMode="decimal"
                    className="h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="combo_description"
                    value={form.combo_description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Combo details..."
                    className="w-full resize-none rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Combo Type
                  </label>
                  <select
                    name="combo_type"
                    value={form.combo_type}
                    onChange={handleChange}
                    className="h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-green-500"
                  >
                    <option value="Configurable">Configurable</option>
                    <option value="Fixed">Fixed</option>
                    <option value="Veg">Veg</option>
                    <option value="Non Veg">Non Veg</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">
                      Configurable
                    </label>
                    <select
                      name="is_configurable"
                      value={form.is_configurable}
                      onChange={handleChange}
                      className="h-10 w-full rounded-lg border bg-white px-3 text-sm"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">
                      Active
                    </label>
                    <select
                      name="is_active"
                      value={form.is_active}
                      onChange={handleChange}
                      className="h-10 w-full rounded-lg border bg-white px-3 text-sm"
                    >
                      <option value="Y">Yes</option>
                      <option value="N">No</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={stepBusy}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {stepBusy ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Plus size={16} />
                  )}
                  {stepBusy ? "Creating..." : "Create & Continue"}
                </button>
              </form>
            </div>
          )}

          {/* STEP 2 */}
          {workflowStep === 2 && (
            <div className="p-5">
              <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3">
                <p className="mt-1 text-sm font-bold text-green-900">
                  {createdCombo?.combo_name}
                </p>
              </div>

              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Option Groups
                </h3>
              </div>

              <div className="space-y-3">
                {draftGroups.map((group, index) => (
                  <div
                    key={group.localId}
                    className="rounded-xl border bg-gray-50 p-3"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-700">
                        Group {index + 1}
                      </span>

                      {draftGroups.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDraftGroup(group.localId)}
                          disabled={stepBusy}
                          className="text-red-500 hover:text-red-600 disabled:opacity-40"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    <input
                      value={group.itemSelectionName}
                      onChange={(event) =>
                        updateDraftGroup(
                          group.localId,
                          "itemSelectionName",
                          event.target.value,
                        )
                      }
                      placeholder="Choose Your Pizza"
                      className="h-9 w-full rounded-lg border bg-white px-3 text-xs outline-none focus:border-green-500"
                    />

                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-gray-500">
                          Min
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={group.minimumSelection}
                          onChange={(event) =>
                            updateDraftGroup(
                              group.localId,
                              "minimumSelection",
                              event.target.value,
                            )
                          }
                          className="h-9 w-full rounded-lg border bg-white px-2 text-xs"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-gray-500">
                          Max
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={group.maximumSelection}
                          onChange={(event) =>
                            updateDraftGroup(
                              group.localId,
                              "maximumSelection",
                              event.target.value,
                            )
                          }
                          className="h-9 w-full rounded-lg border bg-white px-2 text-xs"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[10px] font-semibold text-gray-500">
                          Configurable
                        </label>
                        <select
                          value={group.isConfigurable}
                          onChange={(event) =>
                            updateDraftGroup(
                              group.localId,
                              "isConfigurable",
                              event.target.value,
                            )
                          }
                          className="h-9 w-full rounded-lg border bg-white px-2 text-xs"
                        >
                          <option value="N">No</option>
                          <option value="Y">Yes</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addDraftGroup}
                disabled={stepBusy}
                className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-green-300 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
              >
                <Plus size={14} />
                Add Another Group
              </button>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={resetWorkflow}
                  disabled={stepBusy}
                  className="h-10 rounded-lg border bg-white text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSaveOptionGroups}
                  disabled={stepBusy}
                  className="flex h-10 items-center justify-center gap-2 rounded-lg bg-green-600 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {stepBusy && <Loader2 size={14} className="animate-spin" />}
                  Save Groups
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {workflowStep === 3 && (
            <div className="p-5">
              <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3">
                <p className="text-[10px] font-semibold uppercase text-green-600">
                  Combo #{createdCombo?.combo_id}
                </p>
                <p className="mt-1 text-sm font-bold text-green-900">
                  {createdCombo?.combo_name}
                </p>
              </div>

              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Map Items
                </h3>
              </div>

              <div className="space-y-3">
                {savedGroups.map((group) => {
                  const groupId = getGroupId(group);
                  const selectedIds = asArray(groupSelections[groupId]);

                  return (
                    <div
                      key={groupId || getGroupName(group)}
                      className="rounded-xl border bg-gray-50 p-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-gray-900">
                            {getGroupName(group)}
                          </p>

                          <p className="mt-1 text-[10px] text-gray-500">
                            Group #{groupId} · {getSelectionRule(group)}
                          </p>
                        </div>

                        <span
                          className={`rounded-full px-2 py-1 text-[10px] font-bold ${
                            selectedIds.length > 0
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {selectedIds.length} selected
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => openItemSelector(group)}
                        disabled={stepBusy}
                        className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-green-200 bg-white text-xs font-semibold text-green-700 hover:bg-green-50 disabled:opacity-50"
                      >
                        <Plus size={14} />
                        Select Items
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={resetWorkflow}
                  disabled={stepBusy}
                  className="h-10 rounded-lg border bg-white text-xs font-bold text-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleFinishComboSetup}
                  disabled={stepBusy}
                  className="flex h-10 items-center justify-center gap-2 rounded-lg bg-green-600 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {stepBusy && <Loader2 size={14} className="animate-spin" />}
                  Finish Combo
                </button>
              </div>
            </div>
          )}
        </div>

        {/* =====================================================
            RIGHT - EXISTING COMBOS
           ===================================================== */}
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-100 text-green-700">
                <Layers3 size={17} />
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Combo List
                </h3>
                <p className="text-[11px] text-gray-500">
                  {displayComboList.length} combos · {configurableComboCount}{" "}
                  configurable
                  {masterComboResponse.length > 0
                    ? " · catalogue master response"
                    : getComboOfferResponse.length > 0
                      ? " · getComboOffer response"
                      : ""}
                </p>
              </div>
            </div>
          </div>

          {filteredCombos.length === 0 ? (
            <div className="rounded-2xl border bg-white p-10 text-center text-sm text-gray-500">
              No combos found.
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCombos.map((combo) => {
                const comboId = toText(combo?.combo_id);

                const isExpanded = expandedComboId === comboId;

                const groups = Array.isArray(combo?.optionGroup)
                  ? combo.optionGroup
                  : [];

                return (
                  <div
                    key={comboId || combo?.combo_name}
                    className="overflow-hidden rounded-2xl border bg-white shadow-sm"
                  >
                    <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center">
                      <div className="h-24 w-full shrink-0 overflow-hidden rounded-xl border bg-gray-50 lg:w-32">
                        {combo?.combo_image ? (
                          <img
                            src={combo.combo_image}
                            alt={combo?.combo_name || "Combo"}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-gray-300">
                            <ImageOff size={24} />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-base font-semibold text-gray-900">
                            {combo?.combo_name || "Unnamed Combo"}
                          </h4>

                          <span className="rounded-full bg-green-50 px-2.5 py-1 text-[10px] font-semibold text-green-700">
                            #{comboId || "—"}
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span>{combo?.combo_type || "—"}</span>
                          <span className="font-semibold text-gray-900">
                            ₹{combo?.combo_price || "0"}
                          </span>
                          <span>
                            {groups.length} option group
                            {groups.length === 1 ? "" : "s"}
                          </span>
                        </div>

                        {combo?.combo_description && (
                          <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-600">
                            {combo.combo_description}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setExpandedComboId((current) =>
                            current === comboId ? null : comboId,
                          )
                        }
                        className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg border bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        {isExpanded ? (
                          <ChevronUp size={14} />
                        ) : (
                          <ChevronDown size={14} />
                        )}
                        {isExpanded ? "Hide Details" : "View Details"}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="border-t bg-gray-50/70 p-4">
                        {groups.length === 0 ? (
                          <p className="text-xs text-gray-500">
                            No option groups available.
                          </p>
                        ) : (
                          <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] items-start gap-3">
                            {groups.map((group) => {
                              const groupId = toText(
                                group?.pk_option_group_item_id ??
                                  group?.option_group_item_id,
                              );

                              const items = asArray(group?.items);

                              return (
                                <div
                                  key={groupId || getGroupName(group)}
                                  className="min-w-0 rounded-xl border bg-white p-3 shadow-sm"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p
                                        className="truncate text-xs font-semibold text-gray-900"
                                        title={getGroupName(group)}
                                      >
                                        {getGroupName(group)}
                                      </p>

                                      <p className="mt-0.5 text-[10px] text-gray-500">
                                        #{groupId || "—"} ·{" "}
                                        {getSelectionRule(group)}
                                      </p>
                                    </div>

                                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[9px] font-semibold text-gray-600">
                                      {items.length} item
                                      {items.length === 1 ? "" : "s"}
                                    </span>
                                  </div>

                                  {items.length > 0 && (
                                    <div className="mt-2 grid max-h-[180px] grid-cols-2 gap-1.5 overflow-y-auto pr-1 lg:grid-cols-2">
                                      {items.map((item, itemIndex) => (
                                        <div
                                          key={`${getItemId(
                                            item,
                                          )}-${itemIndex}`}
                                          title={getItemName(item)}
                                          className="min-w-0 truncate rounded-md border bg-gray-50 px-2 py-1.5 text-[10px] font-medium text-gray-700"
                                        >
                                          {getItemName(item)}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          ITEM SELECTOR MODAL
         ===================================================== */}
      {showItemSelector && targetGroup && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Select Items
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  {getGroupName(targetGroup)} · Group #{getGroupId(targetGroup)}
                </p>
              </div>

              <button
                type="button"
                onClick={closeItemSelector}
                className="flex h-9 w-9 items-center justify-center rounded-full border text-gray-500 hover:bg-gray-100"
              >
                <X size={17} />
              </button>
            </div>

            <div className="border-b bg-gray-50 p-4">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  value={itemSearchText}
                  onChange={(event) => setItemSearchText(event.target.value)}
                  placeholder="Search item..."
                  className="h-10 w-full rounded-lg border bg-white pl-9 pr-3 text-sm outline-none focus:border-green-500"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              {filteredSelectableItems.length === 0 ? (
                <div className="py-20 text-center text-sm text-gray-500">
                  No catalogue items found.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {filteredSelectableItems.map((item) => {
                    const itemId = getItemId(item);

                    const groupId = getGroupId(targetGroup);

                    const selected = asArray(groupSelections[groupId]).includes(
                      itemId,
                    );

                    const prices = getItemPrices(item);

                    return (
                      <button
                        key={itemId}
                        type="button"
                        onClick={() => toggleGroupItem(groupId, itemId)}
                        className={`flex min-h-[96px] gap-3 rounded-xl border p-3 text-left transition ${
                          selected
                            ? "border-green-500 bg-green-50 ring-1 ring-green-200"
                            : "border-gray-200 bg-white hover:border-green-300"
                        }`}
                      >
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-gray-50">
                          {getItemImage(item) ? (
                            <img
                              src={getItemImage(item)}
                              alt={getItemName(item)}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-gray-300">
                              <ImageOff size={18} />
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-xs font-bold text-gray-900">
                                {getItemName(item)}
                              </p>

                              <p className="mt-0.5 text-[10px] text-gray-500">
                                Item #{itemId}
                                {item?.__categoryName
                                  ? ` · ${item.__categoryName}`
                                  : ""}
                              </p>
                            </div>

                            <span
                              className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-bold ${
                                selected
                                  ? "border-green-600 bg-green-600 text-white"
                                  : "border-gray-300 bg-white text-transparent"
                              }`}
                            >
                              ✓
                            </span>
                          </div>

                          {prices.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {prices.slice(0, 3).map((price, priceIndex) => (
                                <span
                                  key={`${itemId}-${priceIndex}`}
                                  className="rounded bg-gray-100 px-1.5 py-0.5 text-[9px] text-gray-600"
                                >
                                  {price?.size ||
                                    price?.variantName ||
                                    "Default"}{" "}
                                  · {getPriceLabel(price)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t bg-gray-50 px-5 py-4">
              <p className="text-xs text-gray-500">
                {asArray(groupSelections[getGroupId(targetGroup)]).length}{" "}
                selected
              </p>

              <button
                type="button"
                onClick={closeItemSelector}
                className="h-10 rounded-lg bg-green-600 px-5 text-xs font-bold text-white hover:bg-green-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ComboTab;
